// client/src/coms/interactions/InteractionBrowser.js
// The interactions of one channel, filtered by type and state (active polls, closed polls, upcoming events,
// open tasks, suggestions ...). Choosing one jumps to its message, like a search result. Not a separate
// feature screen: a small dialog over the channel. Server-wide discovery uses Search (interaction:poll ...).
import React, { useEffect, useState } from 'react';
import { openTarget } from '../organisation/orgShared';
import { TYPE_META, TYPE_ORDER, errorText, interactionApi } from './interactionStore';
import './interactions.css';

const STATES = [
  ['', 'All'],
  ['active', 'Active'],
  ['closed', 'Closed'],
  ['upcoming', 'Upcoming'],
  ['open_tasks', 'Open tasks'],
];

function statusLabel(it) {
  if (it.type === 'suggestion') return ({ new: 'New', under_review: 'Under review', planned: 'Planned', in_progress: 'In progress', completed: 'Completed', declined: 'Declined' })[it.suggestion_status] || 'New';
  if (it.status === 'confirmed') return 'Confirmed';
  if (it.status === 'closed') return 'Closed';
  return it.open ? 'Open' : 'Closed';
}

export default function InteractionBrowser({ detail, onClose }) {
  const { serverKey, kind, channelId, channelName } = detail;
  const [type, setType] = useState(detail.type || '');
  const [state, setState] = useState(detail.state || '');
  const [list, setList] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    setList(null);
    setErr('');
    interactionApi
      .list(kind, channelId, { type, state })
      .then((r) => alive && setList((r && r.interactions) || []))
      .catch((e) => {
        if (!alive) return;
        setErr(errorText(e, 'Could not load interactions'));
        setList([]);
      });
    return () => {
      alive = false;
    };
  }, [kind, channelId, type, state]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="vxi-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="vxi-dialog" role="dialog" aria-modal="true" aria-labelledby="vxi-browser-title">
        <div className="vxi-dialog-head">
          <h2 id="vxi-browser-title">
            Interactions
            <div className="vxi-hint" style={{ fontWeight: 600 }}>
              in {kind === 'voice' ? '🔊' : '#'}
              {channelName || 'this channel'}
            </div>
          </h2>
          <button type="button" className="vxi-btn ghost" aria-label="Close" onClick={onClose}>✕</button>
        </div>
        <div className="vxi-dialog-body">
          <div className="vxi-filter-row" role="group" aria-label="Type">
            <button type="button" aria-pressed={!type} onClick={() => setType('')}>All types</button>
            {TYPE_ORDER.map((t) => (
              <button key={t} type="button" aria-pressed={type === t} onClick={() => setType(t)}>
                {TYPE_META[t].icon} {TYPE_META[t].label}
              </button>
            ))}
          </div>
          <div className="vxi-filter-row" role="group" aria-label="State">
            {STATES.map(([v, l]) => (
              <button key={v || 'all'} type="button" aria-pressed={state === v} onClick={() => setState(v)}>{l}</button>
            ))}
          </div>
          {err ? <div className="vxi-error" role="alert">{err}</div> : null}
          {list == null ? (
            <div className="vxi-skeleton" aria-busy="true" />
          ) : list.length ? (
            <div className="vxi-menu" role="list">
              {list.map((it) => (
                <button
                  key={it.id}
                  type="button"
                  role="listitem"
                  className="vxi-list-item"
                  onClick={() => {
                    onClose();
                    openTarget({ kind: 'message', server_key: serverKey, channel_kind: kind, channel_id: Number(channelId), message_id: it.message_id });
                  }}
                >
                  <span aria-hidden="true">{(TYPE_META[it.type] || {}).icon}</span>
                  <span style={{ minWidth: 0 }}>
                    <strong>{it.title}</strong>
                    <span className="vxi-muted">
                      {(TYPE_META[it.type] || {}).label} · {it.created_by_name || 'someone'} · {new Date(it.created_at_ms).toLocaleDateString()}
                    </span>
                  </span>
                  <span className="vxi-chip">{statusLabel(it)}</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="vxi-empty">Nothing matches these filters yet.</div>
          )}
          <div className="vxi-hint" style={{ marginTop: 12 }}>
            Tip: Search finds interactions across channels too — try <code>interaction:poll is:active</code> or <code>interaction:suggestion</code>.
          </div>
        </div>
      </div>
    </div>
  );
}
